import React from 'react';
import {
  alpha,
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  Calculate,
  CheckCircle,
  Restaurant,
} from '@mui/icons-material';

const nutritionRows = [
  {
    parameter: 'Energy',
    per100g: '72 kcal / 302 kJ',
    serving: '72 kcal / 302 kJ',
    rda: '4%',
    labelable: true,
  },
  {
    parameter: 'Fat',
    per100g: '2.0 g',
    serving: '2.0 g',
    rda: '3%',
    labelable: true,
  },
  {
    parameter: 'Saturates',
    per100g: '1.3 g',
    serving: '1.3 g',
    rda: '7%',
    labelable: true,
    nested: true,
  },
  {
    parameter: 'Carbohydrate',
    per100g: '8.6 g',
    serving: '8.6 g',
    rda: '3%',
    labelable: true,
  },
  {
    parameter: 'Sugars',
    per100g: '7.8 g',
    serving: '7.8 g',
    rda: '9%',
    labelable: true,
    nested: true,
  },
  {
    parameter: 'Protein',
    per100g: '5.4 g',
    serving: '5.4 g',
    rda: '11%',
    labelable: true,
  },
  {
    parameter: 'Salt',
    per100g: '0.12 g',
    serving: '0.12 g',
    rda: '2%',
    labelable: true,
  },
];

const claims = [
  {
    label: 'Low fat',
    status: 'Eligible',
    tone: 'success',
  },
  {
    label: 'Source of protein',
    status: 'Eligible',
    tone: 'success',
  },
  {
    label: 'High protein',
    status: 'Not eligible',
    tone: 'default',
  },
];

const nutriScoreLevels = [
  { label: 'A', color: '#038141' },
  { label: 'B', color: '#85BB2F' },
  { label: 'C', color: '#FECB02' },
  { label: 'D', color: '#EE8100' },
  { label: 'E', color: '#E63E11' },
];

function StatusPill({ status, tone }) {
  const styles = {
    success: {
      color: 'success.dark',
      bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
      borderColor: (theme) => alpha(theme.palette.success.main, 0.28),
    },
    warning: {
      color: 'warning.dark',
      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.16),
      borderColor: (theme) => alpha(theme.palette.warning.main, 0.35),
    },
    default: {
      color: 'text.secondary',
      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
      borderColor: (theme) => alpha(theme.palette.grey[500], 0.25),
    },
  };

  const sx = styles[tone] || styles.default;

  return (
    <Box
      sx={{
        px: 1,
        py: 0.35,
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 800,
        lineHeight: 1,
        border: '1px solid',
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {status}
    </Box>
  );
}

function NutritionClaimsVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 340,
        p: 2.5,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          borderRadius: 2.5,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 16px 32px rgba(15, 23, 42, 0.05)',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2,
            py: 1.45,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Restaurant sx={{ fontSize: 17 }} />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Nutrition and claims
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.25 }}
              >
                Nutrition table, claims and Nutrition score estimate
              </Typography>
            </Box>
          </Stack>

          <Chip
            label="100 g edible"
            size="small"
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 800,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            }}
          />
        </Stack>

        {/* Body */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '2.1fr 1fr' },
            minHeight: 255,
          }}
        >
          {/* Main nutrition table */}
          <Box sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.25 }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                }}
              >
                Nutritional table
              </Typography>

              <Stack direction="row" spacing={0.5}>
                {['Real values', 'Inferred'].map((tab, index) => (
                  <Box
                    key={tab}
                    sx={{
                      px: 1,
                      py: 0.45,
                      borderRadius: 1,
                      fontSize: 10,
                      fontWeight: 800,
                      bgcolor: index === 0
                        ? (theme) => alpha(theme.palette.primary.main, 0.1)
                        : (theme) => alpha(theme.palette.grey[500], 0.06),
                      color: index === 0 ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {tab}
                  </Box>
                ))}
              </Stack>
            </Stack>

            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              {/* Table head */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 1fr 0.65fr 0.45fr',
                  columnGap: 1,
                  px: 1.4,
                  py: 1,
                  bgcolor: (theme) => alpha(theme.palette.grey[500], 0.04),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {['Parameter', 'Per 100g', 'Per serving', '% RDA', ''].map((head) => (
                  <Typography
                    key={head}
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 800, fontSize: 10.5 }}
                  >
                    {head}
                  </Typography>
                ))}
              </Box>

              {/* Table rows */}
              {nutritionRows.map((row, index) => (
                <Box
                  key={row.parameter}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 1fr 1fr 0.65fr 0.45fr',
                    columnGap: 1,
                    alignItems: 'center',
                    px: 1.4,
                    py: 0.85,
                    borderBottom: index !== nutritionRows.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: row.nested
                      ? (theme) => alpha(theme.palette.grey[500], 0.035)
                      : 'background.paper',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: row.nested ? 600 : 800,
                      color: row.nested ? 'text.secondary' : 'text.primary',
                      pl: row.nested ? 1.25 : 0,
                      fontSize: 11.5,
                    }}
                  >
                    {row.parameter}
                  </Typography>

                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11.5 }}>
                    {row.per100g}
                  </Typography>

                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11.5 }}>
                    {row.serving}
                  </Typography>

                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11.5 }}>
                    {row.rda}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {row.labelable && (
                      <CheckCircle sx={{ fontSize: 15, color: 'success.main' }} />
                    )}
                  </Box>
                </Box>
              ))}
            </Paper>
          </Box>

          {/* Side panel */}
          <Box
            sx={{
              p: 2,
              borderLeft: { sm: '1px solid rgba(15, 23, 42, 0.08)' },
              borderColor: 'divider',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.018),
            }}
          >
            {/* Compact Nutrition score */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                mb: 1.5,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Calculate sx={{ fontSize: 15 }} />
                </Box>

                <Box>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>
                    Nutrition score estimate
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontSize: 10.5 }}
                  >
                    Current formulation
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={0.45} sx={{ mb: 1 }}>
                {nutriScoreLevels.map((item) => {
                  const active = item.label === 'B';

                  return (
                    <Box
                      key={item.label}
                      sx={{
                        width: 27,
                        height: active ? 24 : 20,
                        borderRadius: 0.8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: active ? 12 : 10.5,
                        fontWeight: 900,
                        color: 'common.white',
                        bgcolor: item.color,
                        opacity: active ? 1 : 0.42,
                        transform: active ? 'translateY(-2px)' : 'none',
                        boxShadow: active
                          ? '0 0 0 2px rgba(37, 99, 235, 0.18)'
                          : 'none',
                      }}
                    >
                      {item.label}
                    </Box>
                  );
                })}
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  display: 'inline-flex',
                  px: 1,
                  py: 0.45,
                  borderRadius: 999,
                  fontWeight: 800,
                  fontSize: 10.5,
                  bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                  color: 'success.dark',
                }}
              >
                Estimated result: B
              </Typography>
            </Paper>

            {/* Claims */}
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'background.paper',
                mb: 1.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 1.1,
                  fontWeight: 800,
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.35,
                }}
              >
                Claims simulation
              </Typography>

              <Stack spacing={0.8}>
                {claims.map((claim) => (
                  <Stack
                    key={claim.label}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 800, fontSize: 11.5 }}
                    >
                      {claim.label}
                    </Typography>

                    <StatusPill status={claim.status} tone={claim.tone} />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default NutritionClaimsVisual;